import { JSX } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import DrawIcon from '@mui/icons-material/Draw';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import { interviewActions } from 'src/redux/slices/Interview/interviewSlice';
import { useIPE } from 'src/redux/slices/Interview/hooks';
import type { InterviewState } from 'src/redux/slices/Interview/types';
import ContentCard from '../components/ContentCard';
import SavedSignatureCard from 'src/components/NewRenterInterface/Signatures/SavedSignatureCard';
import SavedInitialsCard from 'src/components/NewRenterInterface/Signatures/SavedInitialsCard';
import { certTypeDisplayLabel } from 'src/components/NewRenterInterface/utils/certTerminology';

export default function SignatureScreen(): JSX.Element {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { surveyId } = useParams<{ surveyId: string }>();
  const {
    pendingSignatures,
    canSign,
    deferred,
    propertyName,
    certCategory,
    hasStartedInterview,
  } = useSelector((state: { interview: InterviewState }) => state.interview);
  const { callIPE } = useIPE();

  const handleSign = (signatureRequestId: number): void => {
    const returnTo = encodeURIComponent(`/renter/interview/${surveyId}`);
    navigate(
      `/renter/signatures/sign/${signatureRequestId}?returnTo=${returnTo}`
    );
  };

  const handleSignAll = (): void => {
    if (pendingSignatures.length > 0) {
      handleSign(pendingSignatures[0].signature_request_id);
    }
  };

  const handleSignLater = (): void => {
    dispatch(
      interviewActions.deferSignatures(
        pendingSignatures.map((s) => s.signature_request_id)
      )
    );
    dispatch(interviewActions.setLoading(true));
    callIPE({
      skippedQitIds: [...new Set(Object.values(deferred.qitIds).flat())],
    });
  };

  /**
   * Used in proxy mode (!canSign): the actor can't sign on behalf of the
   * subject, so we just move on without recording a deferral. Deferring would
   * add the signature IDs to state.deferred.signatureIds, which later causes
   * resolveWorkItemType to return 'skip_gate' — incorrectly presenting the
   * "you skipped some questions" screen even though nothing was actually skipped.
   *
   * We dispatch setWorkItemType directly instead of calling callIPE() because
   * the backend will never return interview_complete=true while signatures are
   * pending — re-fetching would just route back to 'signature' again.
   */
  const handleContinueProxyMode = (): void => {
    dispatch(
      interviewActions.setWorkItemType(
        hasStartedInterview ? 'complete' : 'nothing_to_do'
      )
    );
  };

  if (!canSign) {
    return (
      <Box
        data-testid="signature-screen"
        sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}
      >
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            display: 'flex',
            justifyContent: { md: 'center' },
            alignItems: { md: 'flex-start' },
            pt: { md: 4 },
          }}
        >
          <ContentCard maxWidth={600}>
            <Box
              sx={{
                p: 3,
                textAlign: 'center',
                alignItems: 'center',
                justifyContent: 'center',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <DrawIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" sx={{ mb: 1 }}>
                Signature required
              </Typography>
              <Typography sx={{ color: 'text.secondary', mb: 4 }}>
                These documents can only be signed by the account holder. Let
                them know to log in and sign from their own profile.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={handleContinueProxyMode}
                data-testid="sign-later-button"
              >
                I understand, continue
              </Button>
            </Box>
          </ContentCard>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      data-testid="signature-screen"
      sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}
    >
      {/* Body — scrollable, centers content on desktop */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          display: 'flex',
          justifyContent: { md: 'center' },
          alignItems: { md: 'flex-start' },
          pt: { md: 4 },
        }}
      >
        <ContentCard maxWidth={600}>
          {/* Saved signature / initials cards */}
          <Stack direction="row" gap={1.5}>
            <SavedSignatureCard isImpersonated={false} />
            <SavedInitialsCard isImpersonated={false} />
          </Stack>

          {/* Signature request card */}
          <Box
            sx={{
              bgcolor: 'background.paper',
              borderRadius: '16px',
              border: '1px solid',
              borderColor: 'divider',
              overflow: 'hidden',
            }}
          >
            {/* Card header */}
            <Box
              sx={{
                py: '16px',
                px: '20px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography
                sx={{ color: 'text.primary', fontSize: 16, fontWeight: 700 }}
              >
                Signature Requested
              </Typography>
              <Button
                data-testid="sign-all-button"
                onClick={handleSignAll}
                sx={{
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  borderRadius: '8px',
                  px: '14px',
                  py: '8px',
                  fontSize: 13,
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  textTransform: 'none',
                  '&:hover': { bgcolor: 'primary.dark' },
                }}
              >
                <DrawIcon
                  sx={{ fontSize: 14, color: 'primary.contrastText' }}
                />
                Sign All
              </Button>
            </Box>

            {/* Divider after header */}
            <Box sx={{ height: '1px', bgcolor: 'divider' }} />

            {/* Form items */}
            <Stack gap={0} sx={{ p: 0 }}>
              {pendingSignatures.map((sig, index) => (
                <Box key={sig.signature_request_id}>
                  <Box
                    sx={{
                      py: '14px',
                      px: '20px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    {/* Left: form name + property + cert type */}
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '3px',
                        minWidth: 0,
                      }}
                    >
                      <Typography
                        sx={{
                          color: 'text.primary',
                          fontSize: 14,
                          fontWeight: 600,
                        }}
                        noWrap
                      >
                        {sig.form_name}
                      </Typography>
                      {propertyName && (
                        <Typography
                          variant="caption"
                          sx={{ color: 'text.secondary' }}
                          noWrap
                        >
                          {propertyName}
                        </Typography>
                      )}
                      {(sig.cert_type_label || certCategory) && (
                        <Typography
                          variant="caption"
                          sx={{ color: 'text.secondary' }}
                          noWrap
                        >
                          {certTypeDisplayLabel(
                            sig.cert_type_label ?? certCategory
                          )}
                        </Typography>
                      )}
                    </Box>

                    {/* Right: Sign button */}
                    <Button
                      data-testid="sign-button"
                      onClick={() => handleSign(sig.signature_request_id)}
                      size="small"
                      variant="outlined"
                      sx={{
                        flexShrink: 0,
                        borderRadius: '8px',
                        borderColor: 'info.main',
                        color: 'info.main',
                        px: '16px',
                        py: '6px',
                        fontSize: 13,
                        fontWeight: 600,
                        textTransform: 'none',
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                    >
                      Sign
                    </Button>
                  </Box>

                  {/* Divider between items (not after last) */}
                  {index < pendingSignatures.length - 1 && (
                    <Box
                      sx={{ height: '1px', bgcolor: 'grey.100', mx: '20px' }}
                    />
                  )}
                </Box>
              ))}
            </Stack>
          </Box>
        </ContentCard>
      </Box>

      {/* Footer — always full width */}
      <Box
        sx={{
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          display: 'flex',
          justifyContent: { xs: 'stretch', md: 'flex-end' },
          alignItems: 'center',
          pt: { xs: 2, md: 0 },
          px: { xs: 2.5, md: 5 },
          pb: { xs: 3, md: 0 },
          height: { xs: 'auto', md: 80 },
        }}
      >
        <Button
          onClick={handleSignLater}
          data-testid="sign-later-button"
          sx={{
            height: 44,
            borderRadius: '12px',
            border: '1px solid',
            borderColor: 'grey.300',
            color: 'text.secondary',
            fontSize: 14,
            fontWeight: 500,
            width: { xs: '100%', md: 'auto' },
            px: { xs: 0, md: 2.5 },
            textTransform: 'none',
          }}
        >
          I&apos;ll sign later
        </Button>
      </Box>
    </Box>
  );
}
